import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp, faAnglesDown, faPlusSquare, faMinusSquare } from "@fortawesome/free-solid-svg-icons";
import "./PositionsUSA.css";
import { gsap } from "gsap";
import useAuth from "../../hooks/useAuth";
import useGlobalParams from "../../hooks/useGlobalParams";
import useUSPosition from "../../hooks/useUSPosition";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate, useLocation } from "react-router-dom";

import ListWithFilters from "../ListWithFilters/ListWithFilters";
import PositionsUSAGeneral from "./PositionsUSAGeneral";
import PositionsUSAPayment from "./PositionsUSAPayment";
import PositionsUSAPackaging from "./PositionsUSAPackaging";
import PositionsUSAInCharge from "./PositionsUSAInCharge";
import PositionsUSADelivery from "./PositionsUSADelivery";
import PositionsUSAFigures from "./PositionsUSAFigures";
import PositionsUSAStorage from "./PositionsUSAStorage";

import * as Calculations from "./utils/calculations";
import { ignoreEnter } from "./utils/inputhandlers";
import { resetCustoms, resetInbound, resetOceanfreight, resetOutbound } from "./utils/resetFunctions";
import useLoadPositions from "./hooks/useLoadPositions";
import { saveNewUSposition, saveEdits } from "./api/APIcalls";
import useLoadPositionToEdit from "./hooks/useLoadPositionToEdit";
import useCalculations from "./hooks/useCalculations";
import useUSUndo from "../../hooks/useUSUndo";
import { undoLastEdit, redoLastEdit } from "./utils/undo";

const PositionsUSA = () => {
  // General
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  // Load context values
  const { auth } = useAuth();
  const { globalparams } = useGlobalParams();
  const {
    USposition,
    setUSposition,
    USpositionData,
    setUSpositionData,
    USpositionInit,
    USpositionDataInit,
    positiontoedit,
    setPositiontoedit,
    paytermInit,
    paytermDataInit,
    paymentterm,
    paymenttermData,
    setPaymentterm,
    setPaymenttermData,
    paytermHash,
    payrow,
    // loaded,
    // setLoaded,
  } = useUSPosition();
  const {
    positionEdits,
    editID,
    setEditID,
    undoList,
    showcalculator,
    setShowcalculator,
    undoFocusFlag,
    setUndoFocusFlag,
    inputRefs,
    reloadpayterms,
  } = useUSUndo();

  //Refs
  const pUSAsearchRef = useRef(JSON.parse(JSON.stringify(paymentterm)));
  const SPMRef = useRef(JSON.parse(JSON.stringify(paymenttermData)));
  const loadedPosition = useRef(USposition);
  const loadedPositionData = useRef(USpositionData);
  const loadedPayterm = useRef(structuredClone(paytermInit));
  const loadedPaytermData = useRef(structuredClone(paytermDataInit));

  // Editing
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [updateLoadedPayterms, setUpdatedLoadedPayterms] = useState(false);

  //SearchField Clear
  const [toggleClearSF, setToggleClearSF] = useState(false);

  //Show Hide Flags
  const [showinfo, setShowinfo] = useState();

  //List Data
  const [reloadlistdata, setReloadlistdata] = useState();

  // Store loaded position to compare edits against
  useEffect(() => {
    loadedPosition.current = USposition;
    loadedPositionData.current = USpositionData;
  }, [loaded]);

  // Update paymentterm and paymenttermdata after loading to include a row value
  useEffect(() => {
    setPaymentterm((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.posPaytermID || payrow.current };
      })
    );
    setPaymenttermData((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.posPaytermID || payrow.current };
      })
    );
    return setUpdatedLoadedPayterms((prev) => !prev);
  }, [loaded]);

  // Store loaded payment terms to compare edits against. Do this after update above.
  useEffect(() => {
    loadedPayterm.current = structuredClone(paymentterm);
    loadedPaytermData.current = structuredClone(paymenttermData);
    payrow.current = 0;
  }, [updateLoadedPayterms]);

  ///////////
  // CLEAR FORM FUNCTIONS
  ///////////

  // Clear Selected Row in List
  const [clearSelectedRowID, setClearSelectedRowID] = useState();
  // Clear Position Function
  const clearUSPosition = () => {
    setUSposition(USpositionInit);
    setUSpositionData(USpositionDataInit);
    setPaymentterm(paytermInit);
    setPaymenttermData(paytermDataInit);
    setPositiontoedit(null);
    setClearSelectedRowID(false);
    setLoaded((prev) => !prev);
    setUpdatedLoadedPayterms((prev) => !prev);
    setToggleClearSF((prev) => !toggleClearSF);
    setEditID(0);
  };

  // Set Insurance Variables when Incoterm changes
  useEffect(() => {
    USposition &&
      Calculations.setInsuranceVars(
        USposition.sincoterm,
        globalparams,
        setUSposition,
        setUSpositionData,
        positionEdits,
        editID
      );
  }, [USposition.sincoterm]);

  //Calculations Hook
  useCalculations(USpositionData, paymenttermData);

  // Load List Data
  const listdata = useLoadPositions(axiosPrivate, reloadlistdata, navigate, location, "loadUSpositions");

  // Show/Hide Position List Component
  const [showPUSASearch, setShowPUSASearch] = useState(false);
  useEffect(() => {
    if (showPUSASearch) {
      const elem = pUSAsearchRef.current;
      gsap.to(elem, {
        bottom: "-1vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
    if (!showPUSASearch) {
      const elem = pUSAsearchRef.current;
      gsap.to(elem, {
        bottom: "-73vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
  }, [showPUSASearch]);

  // Give focus to the undo input field using inputRefs
  useEffect(() => {
    if (undoFocusFlag && inputRefs.current[undoFocusFlag]) {
      inputRefs.current[undoFocusFlag].focus();
      setToggleClearSF((prev) => !prev);
    }
    return setUndoFocusFlag("");
  }, [undoFocusFlag]);

  // Attach Undo and Redo functions with addEventListener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for MetaKey+Z (Undo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        undoLastEdit(
          e,
          setToggleClearSF,
          positionEdits,
          undoList,
          setUSposition,
          setUSpositionData,
          setShowcalculator,
          setUndoFocusFlag,
          loadedPosition,
          loadedPositionData,
          setEditID,
          setPaymentterm,
          setPaymenttermData,
          paymentterm,
          paymenttermData,
          loadedPayterm,
          loadedPaytermData
        );
      }
      // Check for MetaKey+Shift+Z (Redo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        redoLastEdit(
          e,
          setToggleClearSF,
          undoList,
          positionEdits,
          setUSposition,
          setUSpositionData,
          setShowcalculator,
          setUndoFocusFlag,
          setEditID,
          setPaymentterm,
          setPaymenttermData,
          loadedPayterm,
          loadedPaytermData
        );
      }
    };
    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Show Saved Position Message
  const [SPM, setSPM] = useState();
  const [showSPM, setShowSPM] = useState(false);
  const onSPMComplete = () => {
    setSPM(null);
  };
  useEffect(() => {
    const elem = SPMRef.current;
    if (SPM) {
      gsap.fromTo(elem, { opacity: 0.5, top: "-50px" }, { opacity: 1, top: 0, duration: 0.1, ease: "power2.in" });
      gsap.fromTo(
        elem,
        { opacity: 1 },
        {
          opacity: 0,
          duration: 3,
          ease: "power2.inOut",
          onComplete: onSPMComplete,
        }
      );
    }
  }, [showSPM]);

  // Select Position to Edit
  const selectPositionToEdit = (item) => {
    clearUSPosition();
    setPositiontoedit(item.USposID);
    setShowPUSASearch(false);
  };

  // Load Position when Navigating from USQS
  useEffect(() => {
    console.log(location.state?.positiontoedit);
    if (location.state?.positiontoedit) {
      setPositiontoedit(location.state.positiontoedit);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load Position to Edit
  useLoadPositionToEdit(
    axiosPrivate,
    positiontoedit,
    setUSposition,
    setUSpositionData,
    setPaymentterm,
    setPaymenttermData,
    paytermInit,
    paytermDataInit,
    setLoaded,
    navigate,
    location
  );

  // useEffect(() => {
  //   console.log("location.state:", location.state);
  //   console.log("positiontoedit in context:", positiontoedit);
  // }, [location.state, positiontoedit]);

  return (
    <div className='addposUSA-container'>
      <form
        className='addpositionUSA'
        onSubmit={(e) => {
          e.preventDefault();
          if (positiontoedit) {
            saveEdits(
              positionEdits,
              axiosPrivate,
              positiontoedit,
              setReloadlistdata,
              setSPM,
              setShowSPM,
              paymenttermData,
              USposition.priceafterintins
            );
          } else {
            saveNewUSposition(
              axiosPrivate,
              USpositionData,
              paymenttermData,
              setReloadlistdata,
              reloadlistdata,
              setSPM,
              setShowSPM,
              showSPM,
              clearUSPosition
            );
            // clearUSPosition();
          }
        }}
        onClick={(e) => setShowPUSASearch(false)}
      >
        <div className='addposUS-header'>
          <h1>{positiontoedit ? "Edit USA Position:" : "Add New USA Position:"}</h1>
          <div className='posUS-success-msg' ref={SPMRef}>
            {SPM}
          </div>
          <div className='form-buttons'>
            <button
              type='button'
              tabIndex={201}
              onClick={(e) => {
                clearUSPosition();
              }}
            >
              Clear
            </button>
            {!positiontoedit && (
              <button className='mainbutton' type='submit' tabIndex={201}>
                Save New
              </button>
            )}
            {positiontoedit && (
              <button
                // disabled={editing}
                className={positionEdits.current.length > 0 ? "mainbutton" : "mainbutton btndisabled"}
                type='submit'
                tabIndex={201}
              >
                Save Edits
              </button>
            )}
          </div>
        </div>
        <div className='addposUSA-formcontainer'>
          <div className='addposUSA-1'>
            <PositionsUSAGeneral
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculator, showcalculator)}
              clearfield={toggleClearSF}
            />
            <PositionsUSAPayment
              loadedPayterm={loadedPayterm}
              loadedPaytermData={loadedPaytermData}
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculator, showcalculator)}
              clearfield={toggleClearSF}
            />
            <PositionsUSAPackaging clearfield={toggleClearSF} />
          </div>
          <div className='addposUSA-2' style={{ overflow: "visible" }}>
            <PositionsUSAInCharge auth={auth} clearfield={toggleClearSF} />
            <PositionsUSADelivery setShowinfo={setShowinfo} showinfo={showinfo} clearfield={toggleClearSF} />
          </div>
          <div className='addposUSA-3'>
            <div className='addposUSA-31'>
              <PositionsUSAFigures
                ignoreEnter={(e) => ignoreEnter(e, setShowcalculator, showcalculator)}
                setShowcalculator={setShowcalculator}
                showcalculator={showcalculator}
                resetOutbound={(e) =>
                  resetOutbound(
                    USposition,
                    USpositionData,
                    setUSposition,
                    setUSpositionData,
                    USpositionInit,
                    USpositionDataInit,
                    positionEdits,
                    editID,
                    setEditID
                  )
                }
                resetOceanfreight={(e) =>
                  resetOceanfreight(setUSposition, setUSpositionData, USpositionInit, USpositionDataInit)
                }
                resetCustoms={(e) => resetCustoms(setUSposition, setUSpositionData, USpositionInit, USpositionDataInit)}
                resetInbound={(e) => resetInbound(setUSposition, setUSpositionData, USpositionInit, USpositionDataInit)}
                clearfield={toggleClearSF}
              />
            </div>
            <div className='addposUSA-32'>
              <PositionsUSAStorage clearfield={toggleClearSF} ignoreEnter={(e) => ignoreEnter(e)} />
            </div>
          </div>
        </div>
      </form>
      <div className='possearchUSA' ref={pUSAsearchRef} tabIndex={-1}>
        <div className='possearchUSA-header' onClick={(e) => setShowPUSASearch(!showPUSASearch)}>
          {!showPUSASearch ? <FontAwesomeIcon icon={faAnglesUp} /> : <FontAwesomeIcon icon={faAnglesDown} />}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <ListWithFilters
            data={listdata}
            columns={[
              "posDate",
              "USposID",
              "WGP",
              "abbreviation",
              "supplier",
              "shipfrom",
              "shipuntil",
              "portOfLoad",
              "portOfDestination",
              "quantity",
            ]}
            size={["S", "S", "S", "M", "M", "S", "S", "M", "M", "S", "S"]}
            rowID={"USposID"}
            placeHolders={true}
            placeHolderList={[
              "QSDate",
              "USposID",
              "WGP",
              "Product",
              "Supplier",
              "From",
              "To",
              "POL",
              "POD",
              "Quantity",
            ]}
            tabIndex={-1}
            onRowClick={selectPositionToEdit}
            setClearSelectedRowID={setClearSelectedRowID}
            clearSelectedRowID={clearSelectedRowID}
          />
        </div>
      </div>
    </div>
  );
};

export default PositionsUSA;

// // Console log all input names
// useEffect(() => {
//   // Grab all input elements in the DOM
//   const inputs = document.querySelectorAll("input");
//   // Extract and log their `name` attributes
//   const inputNames = Array.from(inputs).map((input) => input.name);
//   // console.log(inputNames);
// }, []);
